var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
var __decorateParam = (index, decorator) => (target, key) => decorator(target, key, index);

// db/dynamic-classes.ts
import { A } from "@adornis/base/env-info.js";
import { logger } from "@adornis/base/logging.js";
import { Arg, Entity, Field, Mutation, Query } from "@adornis/baseql/decorators.js";
import { AdornisEntity } from "@adornis/baseql/entities/adornisEntity.js";
import { constructValue } from "@adornis/baseql/entities/construct.js";
import { MongoEntity } from "@adornis/baseql/entities/mongoEntity.js";
import { getRawCollection } from "@adornis/baseql/server/collections.js";
import { selectionSet } from "@adornis/baseql/utils/queryGeneration.js";
import { LanguageTranslationPair, TranslationEntry } from "@adornis/translation/db/translation-entry.js";
import { setTranslationResolver } from "@adornis/translation/server/resolver/translationEntry.js";
import { validate } from "@adornis/validation/decorators.js";
import { ValidationError } from "@adornis/validation/errors/ValidationError.js";
import { nonOptional } from "@adornis/validation/functions/nonOptional.js";
import { updateDynamicSchema } from "../server/updateDynamicSchema.js";
var DynamicClass = class extends MongoEntity {
  static get allFields() {
    return selectionSet(() => this, 4);
  }
  static getDynamicClassesByClassNames(classNames, collectionNames) {
    return async (gqlFields) => {
      let results = [];
      for (const collectionName of collectionNames) {
        const dynamicClassCollection = await getRawCollection(collectionName);
        const result = await dynamicClassCollection.find({ class: { $in: classNames } }).toArray();
        results = results.concat(result);
      }
      return results;
    };
  }
  static ensureDynamicClass(classData) {
    return async () => {
      try {
        await classData.validate();
      } catch (e) {
        if (e instanceof ValidationError && e.extensions.translationKey === "already_exists")
          return false;
      }
      await classData.create();
      return true;
    };
  }
  static updateDynamicSchema(entityClasses) {
    return updateDynamicSchema(entityClasses);
  }
  static async afterCreate(entity, result) {
    await super.afterCreate(entity, result);
    await this._updateTranslations(entity);
  }
  static async afterUpdate(oldEntity, update, result) {
    await super.afterUpdate(oldEntity, update, result);
    await this._updateTranslations(update);
  }
  static async _updateTranslations(dynamicClass) {
    await Promise.all(
      dynamicClass.attributes.map(
        (attrDef) => attrDef.constructor.addToTranslations(attrDef)
      )
    );
  }
};
DynamicClass._class = "DynamicClass";
DynamicClass._collectionName = "dynamic-classes";
__decorateClass([
  validate(async ({ value, target, key }) => {
    await nonOptional()({ key, value, target });
    if (A.isServer && await (await getRawCollection(DynamicClass._collectionName)).findOne({ class: value, _id: { $ne: target._id } }))
      throw new ValidationError(`dynamic class with name ${value} already exists`, {
        value,
        target,
        key,
        translationKey: "already_exists"
      });
  }),
  Field((type) => String)
], DynamicClass.prototype, "class", 2);
__decorateClass([
  validate((options) => {
    if (options.target.parentClassName)
      return;
    if (!options.value)
      throw new ValidationError("non_optional", { key: options.key });
  }),
  Field((type) => String)
], DynamicClass.prototype, "collectionName", 2);
__decorateClass([
  Field((type) => String)
], DynamicClass.prototype, "parentClassName", 2);
__decorateClass([
  validate(nonOptional()),
  Field((type) => [AttributeDefinition], { default: (v) => v ?? [] })
], DynamicClass.prototype, "attributes", 2);
__decorateClass([
  Query((type) => [DynamicClass]),
  __decorateParam(0, Arg("classNames", (type) => [String])),
  __decorateParam(1, Arg("collectionNames", (type) => [String]))
], DynamicClass, "getDynamicClassesByClassNames", 1);
__decorateClass([
  Mutation(() => Boolean),
  __decorateParam(0, Arg("classData", (type) => DynamicClass))
], DynamicClass, "ensureDynamicClass", 1);
__decorateClass([
  Mutation(() => Boolean, { requestHandlerMeta: { transport: { dontRestart: true } } }),
  __decorateParam(0, Arg("entityClasses", (type) => [String]))
], DynamicClass, "updateDynamicSchema", 1);
DynamicClass = __decorateClass([
  Entity()
], DynamicClass);
var AttributeDefinition = class extends AdornisEntity {
  getBaseqlName() {
    return this.key ?? this.label.toLowerCase().replace(/\W/g, "").replace(/(^[0-9]+)/, "_$1");
  }
  static getBaseQLName(enumAttributeDefinition) {
    const constructed = constructValue(enumAttributeDefinition);
    return constructed.getBaseqlName();
  }
  static async addToTranslations(enumAttributeDefinition) {
    logger.debug({ translations: enumAttributeDefinition.translations }, "(dynamic-class) addToTranslation");
    await Promise.all(
      (enumAttributeDefinition.translations ?? []).map((translation) => {
        return setTranslationResolver(
          translation.language,
          ["field", enumAttributeDefinition.key],
          translation.translation
        )();
      })
    );
  }
};
AttributeDefinition._class = "AttributeDefinition";
__decorateClass([
  validate(nonOptional()),
  Field((type) => String, { default: (v) => v ?? A.getGloballyUniqueID() })
], AttributeDefinition.prototype, "_id", 2);
__decorateClass([
  validate(nonOptional()),
  Field((type) => String)
], AttributeDefinition.prototype, "type", 2);
__decorateClass([
  validate(nonOptional()),
  Field((type) => String)
], AttributeDefinition.prototype, "key", 2);
__decorateClass([
  Field((type) => String)
], AttributeDefinition.prototype, "errorMessage", 2);
__decorateClass([
  Field((type) => String)
], AttributeDefinition.prototype, "placeholder", 2);
__decorateClass([
  Field((type) => String)
], AttributeDefinition.prototype, "label", 2);
__decorateClass([
  Field((type) => Boolean)
], AttributeDefinition.prototype, "isArray", 2);
__decorateClass([
  Field((type) => String)
], AttributeDefinition.prototype, "validation", 2);
__decorateClass([
  validate(nonOptional()),
  Field((type) => String, { default: (v) => v ?? "scalar" })
], AttributeDefinition.prototype, "gqlType", 2);
__decorateClass([
  Field((type) => [LanguageTranslationPair])
], AttributeDefinition.prototype, "translations", 2);
AttributeDefinition = __decorateClass([
  Entity()
], AttributeDefinition);
var EnumAttributeOption = class extends AdornisEntity {
  static resolveID(entity) {
    return entity._id = A.getGloballyUniqueID();
  }
  getBaseqlName() {
    return this.label.toLowerCase().replace(/\W/g, "").replace(/(^[0-9]+)/, "_$1");
  }
  static getBaseQLName(enumAttributeOption) {
    const constructed = constructValue(enumAttributeOption);
    return constructed.getBaseqlName();
  }
  static async addToTranslations(enumAttributeOption) {
    await Promise.all(
      (enumAttributeOption.translations ?? []).map(
        (translation) => TranslationEntry.setTranslation(
          translation.language,
          ["enums", this.getBaseQLName(enumAttributeOption)],
          translation.translation
        )()
      )
    );
  }
};
EnumAttributeOption._class = "EnumAttributeOption";
__decorateClass([
  validate(nonOptional()),
  Field((type) => String)
], EnumAttributeOption.prototype, "label", 2);
__decorateClass([
  Field((type) => [LanguageTranslationPair], { default: (v) => v ?? [] })
], EnumAttributeOption.prototype, "translations", 2);
EnumAttributeOption = __decorateClass([
  Entity()
], EnumAttributeOption);
var EnumAttributeDefinition = class extends AttributeDefinition {
  static async addToTranslations(enumAttributeDefinition) {
    await super.addToTranslations(enumAttributeDefinition);
    await Promise.all(
      (enumAttributeDefinition.options ?? []).map((option) => EnumAttributeOption.addToTranslations(option))
    );
  }
};
EnumAttributeDefinition._class = "EnumAttributeDefinition";
__decorateClass([
  Field((type) => [EnumAttributeOption], { default: (v) => v ?? [] })
], EnumAttributeDefinition.prototype, "options", 2);
EnumAttributeDefinition = __decorateClass([
  Entity()
], EnumAttributeDefinition);
export {
  AttributeDefinition,
  DynamicClass,
  EnumAttributeDefinition,
  EnumAttributeOption
};
